"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const faqs = [{
  question: "Est-ce que je peux participer à seulement une séance?",
  answer: "Le cercle est pensé comme un parcours de 6 semaines, avec une vraie continuité entre les séances et une dynamique de groupe qui se construit au fil du temps. Pour préserver ce cadre intime et cohérent, les participations ponctuelles ne sont pas proposées."
}, {
  question: "C'est destiné à qui?",
  answer: "Le cercle maman-bébé s’adresse aux mamans qui traversent le quatrième trimestre. Au début du cercle, toutes les mamans auront accouché au cours des trois derniers mois. Tu peux rejoindre un cercle dès ta première semaine postpartum et jusqu’aux quatre mois de ton bébé. Nous recommandons de t’inscrire pendant ta grossesse, car les cercles se remplissent souvent rapidement."
}, {
  question: "C'est mon deuxième/troisième bébé. Est-ce que je peux vous joindre?",
  answer: "Bien sûr! Le Cercle accueille aussi bien les mamans qui vivent leur premier postpartum que celles qui en sont à leur deuxième, troisième ou quatrième bébé. Aucun bébé ne se ressemble, et beaucoup de mamans choisissent de faire les choses différemment lors d’un deuxième ou troisième enfant. De plus, accueillir un deuxième ou un troisième bébé vient aussi avec ses propres défis. Il y a de grandes chances que d’autres mamans du groupe vivent des questionnements similaires aux tiens."
}, {
  question: "Qu’est-ce qui est inclus dans le tarif?",
  answer: "Le tarif comprend les 6 rencontres en présentiel du Cercle Maman & Bébé, l’accompagnement par une accompagnante postnatale, les interventions de spécialistes en périnatalité, ainsi que l’accès au groupe WhatsApp privé pendant toute la durée du programme. Il inclut également la visio bonus sur le sommeil du bébé et huiles essentielles en postpartum, des ressources partagées au fil des séances, ainsi qu’une liste de contacts et recommandations utiles pour les mamans de la région. Et surtout: un espace bienveillant et soutenant pour ne pas traverser les premières semaine."
}, {
  question: "J’ai peur que mon bébé pleure ou de ne pas me sentir à l’aise dans un groupe.",
  answer: "C’est complètement normal. Sortir avec un tout-petit, surtout les premières fois, peut être intimidant. Le cercle est justement pensé pour accueillir la vraie vie avec bébé: les pleurs, les tétées, les biberons, les changes, les besoins de bras, les imprévus. Tu n’as pas besoin d’être “prête”, reposée ou parfaitement à l’aise pour venir."
}];
export default function Questions() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="questionsfrequentes" className="bg-background text-foreground py-16 md:py-24 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-8 lg:gap-12">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-default text-xs tracking-widest uppercase text-primary mb-8">
                QUESTIONS FRÉQUENTES
              </div>

              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-10 leading-[1.1] tracking-tight">
                FAQ
              </h2>

              <p className="font-default text-base md:text-lg leading-relaxed mb-10 max-w-md text-foreground">
                Je reste à ta disposition pour toute autre question via le formulaire de contact.
              </p>

              <Link to="/ContactPage">
                <Button variant="outline" size="lg" className="font-default font-medium tracking-wider uppercase text-xs border-foreground text-foreground bg-transparent hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 px-8 py-6 h-auto rounded-lg">
                  Me contacter
                </Button>
              </Link>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-4">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border border-border bg-card transition-all duration-300 group rounded-lg overflow-hidden">
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:border-primary transition-colors">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-primary shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-foreground leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-primary shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-default text-base md:text-[1.0625rem] leading-[1.75] max-w-lg text-foreground">
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}